import {
  PropertyFilterQuery,
  PropertyFilterToken,
} from "@amzn/awsui-collection-hooks";
import { Ard, FusePlan } from "@amzn/fuse-accounting-central-datastore-client";
import { EXTERNAL_SERVICES } from "../constants/appConstants";
import { DiscrepancyCounts } from "src/context/flare/flare-comparison-service";

// Constants for ARD table scroll behavior
export const ARD_ROW_ID_PREFIX = "ard-row-";
export const BASE_STICKY_HEADER_SCROLL_OFFSET = 144;
export const FILTER_ROW_HEIGHT = 44; // Height of the filter chips row when filters are applied
export const STICKY_HEADER_VERTICAL_OFFSET = 10;

/**
 * Calculates the sticky header scroll offset based on whether filters are applied.
 * Note: This currently only handles the case where there is no more than one row of filters.
 * If multiple rows of filter chips are displayed, the offset may not be accurate.
 * @param hasFilters - Whether any filters are currently applied
 * @returns The appropriate scroll offset value
 */
export const getStickyHeaderScrollOffset = (hasFilters: boolean): number => {
  return hasFilters
    ? BASE_STICKY_HEADER_SCROLL_OFFSET + FILTER_ROW_HEIGHT
    : BASE_STICKY_HEADER_SCROLL_OFFSET;
};

/**
 * Generates the element ID for an ARD row
 * @param ardId - The ARD ID
 * @returns The element ID string
 */
export const getArdRowId = (ardId: string): string =>
  `${ARD_ROW_ID_PREFIX}${ardId}`;

/**
 * Evaluates a property filter token against an ARD item
 * @param token - The property filter token to evaluate
 * @param item - The ARD item to evaluate against
 * @param ardDiscrepanciesMap - Optional map of ARD discrepancies for flareDiscrepancies filter
 * @returns boolean indicating if the token matches the item
 */
export const evaluateTokenOnItem = (
  token: PropertyFilterToken,
  item: Ard,
  ardDiscrepanciesMap?: Map<string, DiscrepancyCounts>,
): boolean => {
  // Handle flareDiscrepancies filter
  if (token.propertyKey === "flareDiscrepancies") {
    const discrepancies =
      item.id && ardDiscrepanciesMap
        ? ardDiscrepanciesMap.get(item.id)
        : undefined;

    const filterValue = String(token.value).toLowerCase();

    if (filterValue === "has mismatches") {
      return discrepancies ? discrepancies.mismatches > 0 : false;
    } else if (filterValue === "has missing values") {
      return discrepancies ? discrepancies.missing > 0 : false;
    }

    return false;
  }

  // Handle usecaseName filter - check if any usecase name matches
  if (token.propertyKey === "usecaseName") {
    const usecases = item.usecases || [];
    const usecaseNames = usecases.map((usecase) => usecase.name);

    switch (token.operator) {
      case "=":
        if (Array.isArray(token.value)) {
          return token.value.some((val) => usecaseNames.includes(val));
        }
        return usecaseNames.includes(token.value);

      case "!=":
        if (Array.isArray(token.value)) {
          return !token.value.some((val) => usecaseNames.includes(val));
        }
        return !usecaseNames.includes(token.value);

      default:
        console.warn(
          "Unsupported operator for usecaseName filter:",
          token.operator,
        );
        return false;
    }
  }

  // Handle free text filters
  if (!token.propertyKey) {
    // Only support contains operator for now.
    if (token.operator !== ":") {
      console.warn("Unsupported operator", token.operator);
    }
    return Object.entries(item).some(([key, value]) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(token.value.toLowerCase());
      } else if (key === "associatedPlans" && value && Array.isArray(value)) {
        return value.some(
          (plan: FusePlan) =>
            token.value.length > 5 &&
            (plan.id?.includes(token.value) ||
              plan.accountingRegistrationId?.includes(token.value)),
        );
      }
      return false;
    });
  }

  // Handle property-specific filters
  const key = token.propertyKey as keyof Ard;
  const itemValue = item[key];

  switch (token.operator) {
    case "=":
      if (Array.isArray(token.value)) {
        return token.value.includes(itemValue);
      }
      return itemValue === token.value;

    case "!=":
      if (Array.isArray(token.value)) {
        return !token.value.includes(itemValue);
      }
      return itemValue !== token.value;

    default:
      console.warn("Unsupported operator for property filter:", token.operator);
      return false;
  }
};

/**
 * Evaluates filter query against an ARD item using the specified operation and tokens
 * @param query - Filter query
 * @param item - The ARD item to evaluate against
 * @param ardDiscrepanciesMap - Optional map of ARD discrepancies for flareDiscrepancies filter
 * @returns boolean indicating if the tokens match the item
 */
export const evaluateQueryOnItem = (
  query: PropertyFilterQuery,
  item: Ard,
  ardDiscrepanciesMap?: Map<string, DiscrepancyCounts>,
): boolean => {
  // No filter added
  if (query.tokens.length === 0) {
    return true;
  }

  // Handle operator for multiple filters
  if (query.operation === "and") {
    return query.tokens.every((token) =>
      evaluateTokenOnItem(token, item, ardDiscrepanciesMap),
    );
  } else {
    return query.tokens.some((token) =>
      evaluateTokenOnItem(token, item, ardDiscrepanciesMap),
    );
  }
};

interface TinyUrlResponse {
  short_url: string;
}

export const createShareableTinyUrl = async (): Promise<string> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  const longUrl = window.location.href;

  if (longUrl.includes("localhost") || longUrl.includes("127.0.0.1")) {
    console.warn("Tiny URL service not available for localhost");
    return longUrl;
  }
  try {
    const url = new URL(EXTERNAL_SERVICES.TINY_URL);
    url.search = new URLSearchParams({
      name: longUrl,
      comment: "BART",
    }).toString();

    const response = await fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "include",
      signal: controller.signal,
    });

    if (!response.ok) {
      console.error(`Error: ${response.status} - HTTP request failed`);
      return longUrl;
    }

    const data: TinyUrlResponse = await response.json();
    return data.short_url || longUrl;
  } catch (err) {
    console.error("Error while creating Tiny URL", err);
    return longUrl;
  } finally {
    clearTimeout(timeoutId);
  }
};
